import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/components/AuthContext";
import { billingAPI } from "@/services/api";
import { capturePosthogEvent } from "@/lib/posthog";
import { rememberTryReturn, TRY_PATH } from "@/lib/tryLink";

const analyticsProperties = (workspaceId: string | null) => ({
  workspace_id: workspaceId,
  previous_subscription_status: null,
  extension_days: 4,
  extension_expires_at: null,
});

const TryPage = () => {
  const { user, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const workspaceId = user?.id || null;
    capturePosthogEvent(
      "try_link_opened",
      analyticsProperties(workspaceId),
    );

    if (!user) {
      rememberTryReturn("login");
      capturePosthogEvent(
        "try_link_login_redirected",
        analyticsProperties(null),
      );
      navigate(
        `/login?returnTo=${encodeURIComponent(TRY_PATH)}`,
        { replace: true },
      );
      return;
    }

    void billingAPI
      .claimTrialExtension()
      .then(async () => {
        await refreshUser().catch(() => undefined);
        await queryClient.invalidateQueries({
          queryKey: ["billing-subscription", user.id],
        });
      })
      .catch(() => undefined)
      .finally(() => {
        navigate("/dashboard/overview", { replace: true });
      });
  }, [navigate, queryClient, refreshUser, user]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner variant="circle-filled" size={60} />
    </div>
  );
};

export default TryPage;
